import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { generateCallChecklist } from '../services/geminiService';
import { Plus, Trash2, Save, Loader2, GripVertical, CheckSquare, X, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface Checklist {
    id: string;
    title: string;
    items: string[];
}

interface ChecklistSettingsProps {
    clientId: string;
}

export const ChecklistSettings: React.FC<ChecklistSettingsProps> = ({ clientId }) => {
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // AI State
    const [isGenerating, setIsGenerating] = useState<Record<string, boolean>>({});
    const [aiTopics, setAiTopics] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        if (clientId) {
            fetchChecklists();
        }
    }, [clientId]);

    const fetchChecklists = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('company_call_checklists')
                .select('*')
                .eq('client_id', clientId)
                .order('created_at', { ascending: true });

            if (error) throw error;
            setChecklists(data || []);
        } catch (e: any) {
            console.error('Error fetching checklists:', e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddChecklist = () => {
        const newChecklist: Checklist = {
            id: 'new-' + Date.now(),
            title: 'Nieuwe Checklist',
            items: ['Nieuw item']
        };
        setChecklists([...checklists, newChecklist]);
    };

    const handleUpdateChecklistTitle = (id: string, title: string) => {
        setChecklists(checklists.map(c => c.id === id ? { ...c, title } : c));
    };

    const handleAddItem = (checklistId: string) => {
        setChecklists(checklists.map(c =>
            c.id === checklistId ? { ...c, items: [...c.items, 'Nieuw item'] } : c
        ));
    };

    const handleUpdateItem = (checklistId: string, index: number, value: string) => {
        setChecklists(checklists.map(c => {
            if (c.id === checklistId) {
                const newItems = [...c.items];
                newItems[index] = value;
                return { ...c, items: newItems };
            }
            return c;
        }));
    };

    const handleRemoveItem = (checklistId: string, index: number) => {
        setChecklists(checklists.map(c => {
            if (c.id === checklistId) {
                const newItems = c.items.filter((_, i) => i !== index);
                return { ...c, items: newItems };
            }
            return c;
        }));
    };

    const handleRemoveChecklist = (id: string) => {
        if (confirm('Weet je zeker dat je deze checklist wilt verwijderen?')) {
            setChecklists(checklists.filter(c => c.id !== id));
        }
    };

    const handleGenerateAI = async (checklistId: string) => {
        const topic = aiTopics[checklistId];
        if (!topic || topic.trim() === '') return;

        setIsGenerating(prev => ({ ...prev, [checklistId]: true }));
        try {
            const items = await generateCallChecklist(topic);
            setChecklists(checklists.map(c =>
                c.id === checklistId ? { ...c, items: items } : c
            ));

            // Clear the topic input after generation
            setAiTopics(prev => {
                const newTopics = { ...prev };
                delete newTopics[checklistId];
                return newTopics;
            });
        } catch (error) {
            alert("Fout bij ophalen AI checklist.");
        } finally {
            setIsGenerating(prev => ({ ...prev, [checklistId]: false }));
        }
    };

    const onDragEnd = (result: any, checklistId: string) => {
        if (!result.destination) return;

        const checklist = checklists.find(c => c.id === checklistId);
        if (!checklist) return;

        const items = Array.from(checklist.items);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setChecklists(checklists.map(c => c.id === checklistId ? { ...c, items } : c));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Get all current ones from DB
            const { data: existingData } = await supabase
                .from('company_call_checklists')
                .select('id')
                .eq('client_id', clientId);

            const existingIds = existingData?.map(d => d.id) || [];
            const currentIds = checklists.filter(c => !c.id.startsWith('new-')).map(c => c.id);
            const idsToDelete = existingIds.filter(id => !currentIds.includes(id));

            // Delete removed checklists
            if (idsToDelete.length > 0) {
                await supabase.from('company_call_checklists').delete().in('id', idsToDelete);
            }

            // Upsert remaining/new
            for (const cl of checklists) {
                const payload = {
                    client_id: clientId,
                    title: cl.title,
                    items: cl.items.filter(i => i.trim() !== ''),
                    updated_at: new Date().toISOString()
                };

                if (cl.id.startsWith('new-')) {
                    await supabase.from('company_call_checklists').insert([payload]);
                } else {
                    await supabase.from('company_call_checklists').update(payload).eq('id', cl.id);
                }
            }

            await fetchChecklists();
            setSuccess('Checklists succesvol opgeslagen!');
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            console.error('Vastlopen tijdens opslaan:', e.message);
            setError('Fout bij opslaan: ' + e.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-8 animate-fadeIn">
            {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
            {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}

            <div className="flex items-center justify-between pb-6 border-b border-slate-200">
                <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">Gesprek Checklists</h2>
                    <p className="text-sm text-slate-500 font-medium mt-1">
                        Maak gestructureerde templates (Bv. Intake, Salarisvoorstel) die recruiters direct kunnen afvinken tijdens een live gesprek.
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-slate-900 text-white text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-black transition-colors flex items-center gap-2 shadow-md shadow-slate-200"
                >
                    {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Checklists Opslaan
                </button>
            </div>

            <div className="space-y-6">
                {checklists.map((checklist) => (
                    <div key={checklist.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm relative group">
                        <button
                            onClick={() => handleRemoveChecklist(checklist.id)}
                            className="absolute top-6 right-6 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Verwijder Checklist"
                        >
                            <Trash2 size={16} />
                        </button>

                        <div className="mb-6 flex gap-4 items-center pr-12">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                                <CheckSquare size={20} />
                            </div>
                            <input
                                type="text"
                                value={checklist.title}
                                onChange={(e) => handleUpdateChecklistTitle(checklist.id, e.target.value)}
                                className="text-lg font-black text-slate-900 bg-transparent border-none p-0 focus:ring-0 w-full outline-none placeholder:text-slate-300"
                                placeholder="Checklist Titel (bv. Eerste Intake)"
                            />
                        </div>

                        {/* AI Generator Overlay/Input */}
                        <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100 flex flex-col sm:flex-row gap-3 items-center shadow-sm">
                            <div className="flex items-center gap-2 text-purple-600 shrink-0">
                                <Sparkles size={16} />
                                <span className="font-bold text-xs uppercase tracking-widest">AI Generatie</span>
                            </div>
                            <input
                                type="text"
                                value={aiTopics[checklist.id] || ''}
                                onChange={(e) => setAiTopics(prev => ({ ...prev, [checklist.id]: e.target.value }))}
                                placeholder="Waar gaat de call over? (bijv. 'React Developer Intake')"
                                className="flex-1 w-full text-sm bg-white border border-purple-200 rounded-lg py-2 px-3 outline-none focus:border-purple-400"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleGenerateAI(checklist.id);
                                }}
                            />
                            <button
                                onClick={() => handleGenerateAI(checklist.id)}
                                disabled={isGenerating[checklist.id] || !aiTopics[checklist.id]}
                                className="shrink-0 bg-purple-600 hover:bg-purple-700 text-white font-bold text-[10px] uppercase tracking-widest py-2 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1 shadow-md shadow-purple-200"
                            >
                                {isGenerating[checklist.id] ? <Loader2 size={12} className="animate-spin" /> : "Genereerlijst"}
                            </button>
                        </div>

                        <DragDropContext onDragEnd={(result) => onDragEnd(result, checklist.id)}>
                            <Droppable droppableId={`list-${checklist.id}`}>
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 mb-4">
                                        {checklist.items.map((item, index) => (
                                            <Draggable key={`${checklist.id}-${index}`} draggableId={`${checklist.id}-${index}`} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`flex items-center gap-3 p-2 group/item ${snapshot.isDragging ? 'bg-slate-50 shadow-md ring-1 ring-slate-200 z-50 rounded-lg' : ''}`}
                                                    >
                                                        <div {...provided.dragHandleProps} className="text-slate-300 hover:text-slate-500 cursor-grab">
                                                            <GripVertical size={16} />
                                                        </div>
                                                        <input
                                                            type="text"
                                                            value={item}
                                                            onChange={(e) => handleUpdateItem(checklist.id, index, e.target.value)}
                                                            className="flex-1 text-sm font-medium text-slate-700 bg-transparent border-b border-dashed border-slate-200 focus:border-emerald-500 px-1 py-1 outline-none transition-colors"
                                                            placeholder="Vraag of agendapunt..."
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveItem(checklist.id, index)}
                                                            className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover/item:opacity-100 transition-opacity rounded-md hover:bg-slate-100"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <button
                            onClick={() => handleAddItem(checklist.id)}
                            className="text-xs font-bold uppercase tracking-widest text-emerald-600 hover:text-emerald-700 flex items-center gap-1 mt-2 pl-8"
                        >
                            <Plus size={14} /> Voeg Item Toe
                        </button>
                    </div>
                ))}

                {checklists.length === 0 && (
                    <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50">
                        <CheckSquare size={32} className="text-slate-400 mx-auto mb-3" />
                        <h3 className="text-sm font-bold text-slate-900 mb-1">Geen Checklists</h3>
                        <p className="text-xs text-slate-500 font-medium">Je hebt nog geen gesprekschecklists gedefinieerd.</p>
                    </div>
                )}

                <button
                    onClick={handleAddChecklist}
                    className="w-full py-4 border-2 border-dashed border-slate-300 rounded-2xl text-slate-500 hover:bg-slate-50 hover:border-slate-400 hover:text-slate-700 font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                >
                    <Plus size={16} /> Nieuwe Checklist Aanmaken
                </button>
            </div>
        </div>
    );
};
